import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@apollo/client'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'
import { dateTime } from '@guestgoodie/helpers'

const dayInMilli = 24 * 60 * 60 * 1000

export function useEventOccurences() {
  const from = useRef(dateTime.toDay(new Date()))
  const to = useRef(new Date(from.current.getTime() + 3 * dayInMilli))

  const { data, fetchMore, subscribeToMore, loading } = useQuery(
    AdminAPI.eventOccurence.GET_BETWEEN,
    {
      variables: {
        filters: {
          from: from.current,
          to: to.current
        }
      },
      fetchPolicy: 'network-only'
    }
  )

  const loadBefore = useCallback(() => {
    const to = from.current
    from.current = new Date(to.getTime() - 3 * dayInMilli)

    fetchMore({
      variables: {
        filters: {
          from: from.current,
          to
        }
      },
      updateQuery(previousQueryResult, options) {
        return {
          eventOccurencesBetween: [
            ...options.fetchMoreResult.eventOccurencesBetween,
            ...previousQueryResult.eventOccurencesBetween
          ]
        }
      }
    })
  }, [fetchMore])

  const loadAfter = useCallback(() => {
    const from = to.current
    to.current = new Date(from.getTime() + 3 * dayInMilli)

    fetchMore({
      variables: {
        filters: {
          from,
          to: to.current
        }
      },
      updateQuery(previousQueryResult, options) {
        return {
          eventOccurencesBetween: [
            ...previousQueryResult.eventOccurencesBetween,
            ...options.fetchMoreResult.eventOccurencesBetween
          ]
        }
      }
    })
  }, [fetchMore])

  // TODO: Should be maybe replace with useSubscription
  useEffect(() => {
    subscribeToMore({
      document: AdminAPI.eventOccurence.UPDATED,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev

        // We need to filter out all items that are not Service items
        const eventOccurence = subscriptionData.data.eventOccurenceUpdated

        let didEditOrder = false

        const items = prev.eventOccurencesBetween.map(function (item) {
          if (item.id === eventOccurence.id) {
            didEditOrder = true

            return eventOccurence
          }

          return item
        })

        return {
          eventOccurencesBetween: didEditOrder
            ? items
            : [eventOccurence, ...items]
        }
      }
    })
  }, [subscribeToMore])

  const occurences = useMemo(() => {
    if (!data) return []

    return data.eventOccurencesBetween
      .map(function (occ) {
        return getFragment(
          SharedAPI.eventOccurence.EVENT_OCCURENCE_FRAGMENT,
          occ
        )
      })
      .toSorted(
        (occA, occB) =>
          new Date(occA.startAt).getTime() - new Date(occB.startAt).getTime()
      )
  }, [data])

  return {
    occurences,
    loading,
    loadBefore,
    loadAfter
  }
}
